import { Component, OnInit, OnDestroy, Input, ElementRef, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { LanguageService, Language } from '../../../services/language.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-language-selector',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="dropdown" [class.dark-bg]="isDarkBackground" [class.light-bg]="!isDarkBackground">
      <button class="btn dropdown-toggle" 
              [class.btn-outline-secondary]="!isDarkBackground"
              [class.btn-outline-light]="isDarkBackground"
              type="button" 
              (click)="toggleDropdown()"
              data-bs-toggle="dropdown" 
              aria-expanded="false">
        <i class="fas fa-globe me-2"></i>
        <span class="language-text">{{ getSelectedLanguage()?.name }}</span>
      </button>

      <ul class="dropdown-menu" [class.show]="showDropdown">
        <li *ngFor="let language of languages" 
            (click)="selectLanguage(language.code)">
          <a class="dropdown-item" 
             [class.active]="language.code === currentLanguage"
             href="javascript:void(0)">
            <span class="flag-icon me-2">{{ language.flag }}</span>
            <span class="language-name">{{ language.name }}</span>
          </a>
        </li>
      </ul>
    </div>
  `,
  styles: [`
    .dropdown {
      position: relative;
    }

    /* Light Background Styles (White/Transparent backgrounds) */
    .dropdown.light-bg .btn-outline-secondary {
      color: #6c757d;
      border-color: #6c757d;
      background-color: rgba(255, 255, 255, 0.9);
      backdrop-filter: blur(10px);
      padding: 0.375rem 0.75rem;
      font-size: 0.875rem;
      line-height: 1.5;
      border-radius: 0.375rem;
      transition: all 0.15s ease-in-out;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }

    .dropdown.light-bg .btn-outline-secondary:hover {
      color: #fff;
      background-color: #6c757d;
      border-color: #6c757d;
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
    }

    .dropdown.light-bg .btn-outline-secondary:focus {
      box-shadow: 0 0 0 0.25rem rgba(108, 117, 125, 0.25);
    }

    /* Dark Background Styles (Colored/Dark backgrounds) */
    .dropdown.dark-bg .btn-outline-light {
      color: #f8f9fa;
      border-color: #f8f9fa;
      background-color: rgba(0, 0, 0, 0.2);
      backdrop-filter: blur(10px);
      padding: 0.375rem 0.75rem;
      font-size: 0.875rem;
      line-height: 1.5;
      border-radius: 0.375rem;
      transition: all 0.15s ease-in-out;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    }

    .dropdown.dark-bg .btn-outline-light:hover {
      color: #000;
      background-color: #f8f9fa;
      border-color: #f8f9fa;
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
    }

    .dropdown.dark-bg .btn-outline-light:focus {
      box-shadow: 0 0 0 0.25rem rgba(248, 249, 250, 0.25);
    }

    /* Common Button Styles */
    .dropdown-toggle::after {
      display: inline-block;
      margin-left: 0.255em;
      vertical-align: 0.255em;
      content: "";
      border-top: 0.3em solid;
      border-right: 0.3em solid transparent;
      border-bottom: 0;
      border-left: 0.3em solid transparent;
    }

    /* Light background dropdown arrow */
    .dropdown.light-bg .dropdown-toggle::after {
      border-top-color: #6c757d;
    }

    /* Dark background dropdown arrow */
    .dropdown.dark-bg .dropdown-toggle::after {
      border-top-color: #f8f9fa;
    }

    /* Dropdown Menu - Always visible on both backgrounds */
    .dropdown-menu {
      position: absolute;
      top: 100%;
      left: 0;
      z-index: 1000;
      display: none;
      min-width: 10rem;
      padding: 0.5rem 0;
      margin: 0.125rem 0 0;
      font-size: 0.875rem;
      color: #212529;
      text-align: left;
      list-style: none;
      background-color: #fff;
      background-clip: padding-box;
      border: 1px solid rgba(0, 0, 0, 0.175);
      border-radius: 0.375rem;
      box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
    }

    .dropdown-menu.show {
      display: block;
    }

    .dropdown-item {
      display: block;
      width: 100%;
      padding: 0.25rem 1rem;
      clear: both;
      font-weight: 400;
      color: #212529;
      text-align: inherit;
      text-decoration: none;
      white-space: nowrap;
      background-color: transparent;
      border: 0;
      cursor: pointer;
      transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out;
    }

    .dropdown-item:hover {
      color: #1e2125;
      background-color: #f8f9fa;
    }

    .dropdown-item.active {
      color: #fff;
      text-decoration: none;
      background-color: #0d6efd;
    }

    .dropdown-item:active {
      color: #fff;
      text-decoration: none;
      background-color: #0d6efd;
    }

    .flag-icon {
      font-size: 1.1em;
    }

    .language-text {
      font-weight: 500;
    }

    .language-name {
      font-weight: 400;
    }

    /* Responsive adjustments */
    @media (max-width: 768px) {
      .dropdown.light-bg .btn-outline-secondary,
      .dropdown.dark-bg .btn-outline-light {
        padding: 0.25rem 0.5rem;
        font-size: 0.8rem;
      }
      
      .dropdown-menu {
        min-width: 8rem;
      }
      
      .dropdown-item {
        padding: 0.2rem 0.75rem;
        font-size: 0.8rem;
      }
    }
  `]
})
export class LanguageSelectorComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() isDarkBackground = false;
  languages: Language[] = [];
  currentLanguage = 'en';
  showDropdown = false;
  private subscriptions: Subscription[] = [];

  constructor(
    private languageService: LanguageService,
    private elementRef: ElementRef
  ) {}

  ngOnInit(): void {
    this.subscriptions.push(
      this.languageService.languages$.subscribe(languages => {
        this.languages = languages;
      }),
      this.languageService.currentLanguage$.subscribe(language => {
        this.currentLanguage = language;
      })
    );
  }

  ngAfterViewInit(): void {
    // this.detectBackgroundColor();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  // private detectBackgroundColor(): void {
  //   // Get the parent element's background color
  //   const parentElement = this.elementRef.nativeElement.parentElement;
  //   if (parentElement) {
  //     const computedStyle = window.getComputedStyle(parentElement);
  //     const backgroundColor = computedStyle.backgroundColor;
      
  //     // Check if background is dark or light
  //     this.isDarkBackground = this.isDarkColor(backgroundColor);
  //   }
  // }

  // private isDarkColor(color: string): boolean {
  //   // Handle different color formats
  //   if (color === 'transparent' || color === 'rgba(0, 0, 0, 0)') {
  //     // Check parent's background recursively
  //     const parentElement = this.elementRef.nativeElement.parentElement?.parentElement;
  //     if (parentElement) {
  //       const computedStyle = window.getComputedStyle(parentElement);
  //       return this.isDarkColor(computedStyle.backgroundColor);
  //     }
  //     return false; // Default to light
  //   }

  //   // Convert color to RGB values
  //   let r = 0, g = 0, b = 0;
  //   let colorParsed = false;
    
  //   if (color.startsWith('rgb')) {
  //     const match = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  //     if (match) {
  //       r = parseInt(match[1]);
  //       g = parseInt(match[2]);
  //       b = parseInt(match[3]);
  //       colorParsed = true;
  //     }
  //   } else if (color.startsWith('#')) {
  //     const hex = color.slice(1);
  //     if (hex.length === 6) {
  //       r = parseInt(hex.slice(0, 2), 16);
  //       g = parseInt(hex.slice(2, 4), 16);
  //       b = parseInt(hex.slice(4, 6), 16);
  //       colorParsed = true;
  //     }
  //   }

  //   // If color couldn't be parsed, default to light
  //   if (!colorParsed) {
  //     return false;
  //   }

  //   // Calculate luminance
  //   const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  //   return luminance < 0.5; // Dark if luminance < 0.5
  // }

  toggleDropdown(): void {
    this.showDropdown = !this.showDropdown;
  }

  selectLanguage(languageCode: string): void {
    this.languageService.setLanguage(languageCode);
    this.showDropdown = false;
  }

  getSelectedLanguage(): Language | undefined {
    return this.languages.find(lang => lang.code === this.currentLanguage);
  }
} 